import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Loader2, Lock, Mail, Phone, Star } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useApp } from "@/contexts/AppContext";
import UserAvatar from "@/components/UserAvatar";
import DealCheckCard from "@/components/DealCheckCard";

interface Contact { phone: string | null; whatsapp: string | null; email: string | null }
interface Props { listingId: string; title: string; ownerId: string; ownerName: string }

const ERRORS: Record<string, string> = {
  rate_limited: "Você atingiu o limite de contatos revelados nas últimas 24 horas. Tente novamente mais tarde.",
  user_blocked: "Sua conta está bloqueada e não pode revelar contatos.",
  listing_unavailable: "Este anúncio não está disponível no momento.",
  listing_not_found: "Anúncio não encontrado.",
};

/** (47) 99812-4433 -> 5547998124433 */
function waNumber(raw: string) {
  const d = raw.replace(/\D/g, "");
  if (!d) return "";
  return d.length <= 11 ? "55" + d : d;
}

function WhatsIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7-2.8-1.1-4.6-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.9s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.3.5-.4.4c-.1.1-.3.3-.1.6.2.3.7 1.2 1.6 2 1.1 1 2 1.3 2.3 1.4.3.1.5.1.6-.1l.9-1c.2-.3.4-.2.6-.1l1.9.9c.3.1.5.2.5.3.1.2.1.7-.1 1.3z" />
    </svg>
  );
}

export default function ContactReveal({ listingId, title, ownerId, ownerName }: Props) {
  const { user, revealedContacts, revealContact } = useApp();
  const navigate = useNavigate();
  const [state, setState] = useState<{ id: string; data: Contact } | null>(null);
  const contact = state?.id === listingId ? state.data : null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const isOwner = user?.id === ownerId;
  const already = isOwner || revealedContacts.includes(listingId);

  async function load() {
    setLoading(true);
    setError("");
    const { data, error: err } = await supabase.rpc("reveal_contact", { p_listing_id: listingId });
    setLoading(false);
    if (err) {
      const msg = err.message || "";
      if (msg.includes("not_authenticated")) { navigate("/login"); return; }
      const key = Object.keys(ERRORS).find((k) => msg.includes(k));
      setError(key ? ERRORS[key] : "Não foi possível revelar o contato. Tente novamente.");
      return;
    }
    setState({ id: listingId, data: data as Contact });
    if (!isOwner) revealContact(listingId);
  }

  // quem ja revelou antes (em qualquer aparelho) ve o contato direto
  useEffect(() => {
    if (user && already && !contact && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, listingId, already]);

  function onReveal() {
    if (!user) { navigate("/login"); return; }
    load();
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard indisponivel */ }
  }

  if (!contact) {
    return (
      <div>
        <button onClick={onReveal} disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#e0a021] px-4 py-3 text-sm font-semibold text-[#3b2a06] transition hover:brightness-95 disabled:opacity-70">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
          Ver contato gratuitamente
        </button>
        {error ? (
          <p className="mt-2 text-center text-xs text-[#c0493b]">{error}</p>
        ) : (
          <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] text-[#8a9088]">
            <Lock className="h-3 w-3" /> Ao revelar, você também poderá avaliar o anunciante
          </p>
        )}
      </div>
    );
  }

  const wa = contact.whatsapp ? waNumber(contact.whatsapp) : "";
  const link = `https://www.agroaluga.com.br${window.location.pathname}`;
  const waText = encodeURIComponent(`Olá! Vi seu anúncio "${title}" no AgroAluga e tenho interesse: ${link}`);
  const tel = contact.phone ? contact.phone.replace(/[^\d+]/g, "") : "";
  const shown = contact.whatsapp || contact.phone || "";
  const nothing = !contact.phone && !contact.whatsapp && !contact.email;

  return (
    <div className="pt-1">
      <div className="flex items-center gap-3">
        <UserAvatar userId={ownerId} name={ownerName} size="md" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[#1f2621]">{ownerName}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#2d7a4d]">Contato do anunciante</p>
        </div>
      </div>

      {nothing && <p className="mt-3 text-sm text-[#8a9088]">O anunciante não cadastrou telefone nem e-mail. Use o botão de mensagem.</p>}

      {wa && (
        <a href={`https://wa.me/${wa}?text=${waText}`} target="_blank" rel="noopener noreferrer"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1fa855] px-4 py-3 whitespace-nowrap text-sm font-semibold text-white transition hover:brightness-95">
          <WhatsIcon className="h-[18px] w-[18px]" /> Chamar no WhatsApp
        </a>
      )}

      {(tel || contact.email) && (
        <div className="mt-2 flex gap-2">
          {tel && (
            <a href={`tel:${tel}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#2d7a4d] bg-white px-3 py-2.5 text-[13px] font-semibold text-[#2d7a4d] transition hover:bg-[#eaf2ec]">
              <Phone className="h-3.5 w-3.5" /> Ligar
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}?subject=${encodeURIComponent("Interesse no anúncio: " + title)}`}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-[#2d7a4d] bg-white px-3 py-2.5 text-[13px] font-semibold text-[#2d7a4d] transition hover:bg-[#eaf2ec]">
              <Mail className="h-3.5 w-3.5" /> E-mail
            </a>
          )}
        </div>
      )}

      {shown && (
        <button onClick={() => copy(shown)}
          className="mt-2.5 flex w-full items-center justify-between rounded-xl border border-dashed border-[#ebe7dc] bg-[#f7f5ef] px-3 py-2.5 text-sm font-semibold text-[#1f2621]">
          <span className="whitespace-nowrap">{shown}</span>
          <span className="flex items-center gap-1 text-xs font-semibold text-[#2d7a4d]">
            {copied ? <><Check className="h-3.5 w-3.5" /> Copiado</> : <><Copy className="h-3.5 w-3.5" /> Copiar</>}
          </span>
        </button>
      )}
      {contact.email && shown && <p className="mt-1.5 truncate text-center text-xs text-[#8a9088]">{contact.email}</p>}

      {!isOwner && <DealCheckCard listingId={listingId} className="mt-3" />}

      {!isOwner && (
        <div className="mt-3 flex gap-2 rounded-xl bg-[#eaf2ec] px-3 py-2.5 text-[12px] leading-snug text-[#1a4a2e]">
          <Star className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Depois do aluguel, volte aqui e <b>avalie o anunciante</b>. Isso ajuda outros produtores.</span>
        </div>
      )}
    </div>
  );
}
